import { useState, type ReactNode } from "react";
import {
  ApiError,
  Badge,
  Button,
  Spinner,
  apiFetch,
  type PortalMailDownload,
  type PortalMailItem,
} from "@dearbill/shared";
import { fmtRelativeDay } from "../dashboardFormat";
import { usePortalFeed } from "../hooks/usePortalFeed";
import { openPendingTab } from "../pendingTab";
import { HomeCard } from "./HomeCard";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** The server's plan-correspondence label; anything else chips as "Other". */
const PLAN_CORRESPONDENCE_LABEL = "plan_correspondence";

function mailKey(item: PortalMailItem): string {
  return item.messageId;
}

interface MailCardProps {
  /** Portal session bearer token. */
  session: string;
  /** Called when the session is rejected (401). */
  onSignOut: () => void;
}

/**
 * The dashboard Mail section: newest-first pages of `GET /api/portal/mail`,
 * with the same "Show more" cursor contract as the Timeline. Tapping a row
 * presigns the raw message through `GET /api/portal/mail/{id}/download` and
 * opens the URL in a tab reserved during the tap (`openPendingTab`). A 503
 * means mail is not configured for this environment, so the whole card renders
 * nothing rather than an error. Newly received mail appears without a reload:
 * `usePortalFeed` refreshes page one on focus and on an interval.
 */
export function MailCard({ session, onSignOut }: MailCardProps): ReactNode {
  const { items, hasMore, loadingMore, error, inert, loadMore, retry } = usePortalFeed(
    "/api/portal/mail",
    session,
    onSignOut,
    mailKey,
  );
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState(false);

  const openMessage = (messageId: string): void => {
    // Reserved before the fetch, while the tap still counts as a user gesture.
    const tab = openPendingTab();
    setOpeningId(messageId);
    setOpenError(false);
    apiFetch<PortalMailDownload>(
      `/api/portal/mail/${encodeURIComponent(messageId)}/download`,
      {},
      { baseUrl: API_BASE, token: session },
    )
      .then(({ url }) => {
        // The presigned URL carries an attachment disposition, so the new tab
        // downloads the message rather than navigating the portal away.
        tab.navigate(url);
      })
      .catch((err: unknown) => {
        tab.close();
        if (err instanceof ApiError && err.status === 401) {
          onSignOut();
          return;
        }
        setOpenError(true);
      })
      .finally(() => setOpeningId(null));
  };

  if (inert) return null;

  let body: ReactNode;
  if (items === null && !error) {
    body = (
      <div className="text-slate-teal flex items-center gap-3">
        <Spinner /> <span>Loading your mail…</span>
      </div>
    );
  } else if (items === null) {
    body = (
      <p className="text-body-sm text-slate-teal">
        We couldn’t load your mail.{" "}
        <button
          type="button"
          onClick={retry}
          className="text-primary-text cursor-pointer font-semibold underline hover:no-underline"
        >
          Try again
        </button>
      </p>
    );
  } else if (items.length === 0) {
    body = (
      <p className="text-body-sm text-slate-teal">
        No mail yet — letters from your plan will show up here as they arrive.
      </p>
    );
  } else {
    body = (
      <>
        <ul className="flex flex-col">
          {items.map((item) => (
            <li key={item.messageId} className="border-haze border-b last:border-b-0">
              <button
                type="button"
                onClick={() => openMessage(item.messageId)}
                disabled={openingId !== null}
                className="hover:bg-ice flex w-full cursor-pointer items-start justify-between gap-4 py-4 text-left"
              >
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="text-body-sm text-midnight font-medium">
                    {openingId === item.messageId ? "Opening…" : item.subject}
                  </span>
                  <span className="text-caption text-slate-teal">
                    {item.fromDomain} · {fmtRelativeDay(item.receivedAt)}
                  </span>
                </span>
                <Badge
                  tone={item.label === PLAN_CORRESPONDENCE_LABEL ? "active" : "neutral"}
                  className="shrink-0"
                >
                  {item.label === PLAN_CORRESPONDENCE_LABEL ? "Plan correspondence" : "Other"}
                </Badge>
              </button>
            </li>
          ))}
        </ul>
        {openError && (
          <p role="alert" className="text-body-sm text-danger">
            We couldn’t open that message. Please try again.
          </p>
        )}
        {hasMore && (
          <Button
            variant="secondary"
            size="md"
            className="self-start"
            disabled={loadingMore}
            onClick={loadMore}
          >
            {loadingMore ? "Loading…" : "Show more"}
          </Button>
        )}
        {error && (
          <p role="alert" className="text-body-sm text-danger">
            We couldn’t load more right now. Please try again.
          </p>
        )}
      </>
    );
  }

  return (
    <HomeCard title="Mail" titleSize="lg">
      {body}
    </HomeCard>
  );
}
